import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthProvider.jsx'
import { BrandLockup } from './Brand.jsx'
import Watermark from './Watermark.jsx'
import { listNotifications } from '../api/notifications.js'

/**
 * Authenticated app shell. Compact, branded header bar with the Wantok
 * lockup, single-line user identity, role chip, and an icon sign-out.
 * Subtle radial noise overlay + coral accent hairline give the header
 * weight without adding height.
 */
export default function Layout() {
  const { user, signOut } = useAuth()
  const nav = useNavigate()

  async function handleOut() {
    await signOut()
    nav('/login', { replace: true })
  }

  const fullName = user?.full_name || user?.name || user?.email
  const role = user?.role || 'user'

  return (
    <div className="min-h-screen flex flex-col text-slate-900 relative">
      {/* ICER Health watermark — sits behind every authenticated page.
          Fixed-position, low opacity, doesn't intercept clicks. Hidden
          on print so the watermark on /print pages is the printable
          one (own opacity tuned for paper). */}
      <Watermark />
      {/*
        `print:hidden` strips the whole app header from the printed
        page. Without this, the Print Encounter view shows the logo
        TWICE — once in the app header, once in the document
        letterhead — which looks unprofessional on the printout.
      */}
      <header className="relative z-10 bg-wantok-gradient text-white shadow-[0_6px_20px_-8px_rgba(15,23,42,0.35)] print:hidden">
        {/* Subtle highlight along the top edge for glassy depth */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
        {/* Soft radial sheen, picks up the brand mark side */}
        <div className="pointer-events-none absolute inset-0 opacity-60"
             style={{ background: 'radial-gradient(800px 240px at 18% 0%, rgba(45,212,191,0.18), transparent 60%)' }} />
        {/* Faint dot grain — adds texture without noise */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.07]"
             style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)', backgroundSize: '14px 14px' }} />

        <div className="relative max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
          {/* size={48} so the white-badge logo carries weight on the
              teal header without losing the iCH wordmark inside it. */}
          <BrandLockup variant="light" linkTo="/" size={48} />

          {/* Platform-admin nav — visible only when role === 'admin'.
              Hidden on phones to keep the bar compact; admins typically
              demo from a laptop anyway. */}
          {role === 'admin' && (
            <nav className="hidden md:flex items-center gap-1">
              <NavPill to="/admin-invitations">Doctors</NavPill>
              <NavPill to="/admin-staff-invitations">Staff</NavPill>
              <NavPill to="/superuser/org-dashboard">Per-org</NavPill>
              <NavPill to="/superuser/national-dashboard">National</NavPill>
              <NavPill to="/superuser/platform-costs">Costs</NavPill>
              <NavPill to="/superuser/screening">Screening</NavPill>
              <NavPill to="/superuser/audit-log">Audit</NavPill>
            </nav>
          )}

          {user && (
            <div className="flex items-center gap-2">
              {/* Single-line identity: name (links to /settings) +
                  role chip + sign-out icon. The name doubles as the
                  entry point to profile + password settings — same
                  pattern as Slack / Notion / Linear. Discoverable via
                  underline-on-hover so it doesn't read as plain text. */}
              <Link
                to="/settings"
                title="Account settings"
                className="hidden sm:inline-block text-sm font-medium text-white/95 max-w-[14rem] truncate hover:underline decoration-white/60 underline-offset-2"
              >
                {fullName}
              </Link>
              <RoleChip role={role} />
              <NotificationBell role={role} />
              <Link
                to="/settings"
                title="Settings"
                aria-label="Account settings"
                className="ml-1 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/25 border border-white/15 transition-colors"
              >
                <SettingsIcon />
              </Link>
              <button
                onClick={handleOut}
                title="Sign out"
                className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/25 border border-white/15 transition-colors"
                aria-label="Sign out"
              >
                <SignOutIcon />
              </button>
            </div>
          )}
        </div>

        {/* Bottom accent hairline: deep teal → coral → deep teal */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px]"
          style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(20,184,166,0.6) 30%, rgba(238,77,31,0.7) 50%, rgba(20,184,166,0.6) 70%, transparent 100%)' }}
        />
      </header>

      {/* relative z-10 so the page content sits above the Watermark
          fixed-positioned at z-0 underneath everything. */}
      <main className="relative z-10 flex-1 w-full max-w-6xl mx-auto px-3 sm:px-5 py-5 sm:py-6">
        <Outlet />
      </main>

      <footer className="mt-6 py-4 border-t border-sand-200/60">
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-slate-500">
          <div className="flex items-center gap-2">
            <span className="font-display font-semibold text-slate-700">ICER Health</span>
            <span className="text-slate-300">·</span>
            <span>Wantok Telehealth</span>
            <span className="text-slate-300">·</span>
            <span>© 2026</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
              Data residency: PNG
            </span>
            <span className="text-slate-300 hidden sm:inline">·</span>
            <span className="hidden sm:inline">FHIR R4 · ICD-10 · HIPAA-aligned</span>
          </div>
        </div>
      </footer>
    </div>
  )
}

/**
 * Single source of role-truth in the header. Compact pill, role-specific
 * accent, no duplicate text below it.
 */
function RoleChip({ role }) {
  const map = {
    doctor:           'bg-wantok-50 text-wantok-800 border-wantok-200/80',
    specialist:       'bg-wantok-50 text-wantok-800 border-wantok-200/80',
    patient:          'bg-coral-50 text-coral-700 border-coral-200',
    admin:            'bg-slate-900 text-white border-slate-900',
    pharmacy_staff:   'bg-amber-50 text-amber-800 border-amber-200',
    laboratory:       'bg-indigo-50 text-indigo-700 border-indigo-200',
    radiology:        'bg-violet-50 text-violet-700 border-violet-200',
    ambulance_staff:  'bg-rose-50 text-rose-700 border-rose-200',
    lab_staff:        'bg-indigo-50 text-indigo-700 border-indigo-200',
    radiology_staff:  'bg-violet-50 text-violet-700 border-violet-200',
    // Subscribing-organization admin (HR / workforce-wellness lead) —
    // emerald to read as "value" / "savings", matches the dashboard.
    org_admin:        'bg-emerald-50 text-emerald-800 border-emerald-200',
  }
  const cls = map[role] || 'bg-white/15 text-white border-white/25'
  return (
    <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] border font-bold whitespace-nowrap ${cls}`}>
      {(role || 'user').replaceAll('_', ' ')}
    </span>
  )
}

/**
 * Header nav pill. Uses NavLink so the active route is visually
 * distinct (subtle white wash) without needing to inspect the URL.
 */
function NavPill({ to, children }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `text-[11px] uppercase tracking-[0.18em] font-bold whitespace-nowrap ` +
        `px-3 py-1.5 rounded-full border transition-colors ` +
        (isActive
          ? 'bg-white/20 text-white border-white/30'
          : 'text-white/80 hover:text-white border-white/0 hover:bg-white/10 hover:border-white/15')
      }
    >
      {children}
    </NavLink>
  )
}

function SignOutIcon() {
  return (
    <svg viewBox="0 0 20 20" width="14" height="14" className="text-white/90">
      <path
        d="M8 4H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3M13 7l3 3-3 3M16 10H8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Header notification bell with an unread-count badge. Polls the
 * notifications endpoint every 30s. Click → goes to the role's home
 * page, where the existing Notifications card on each role's
 * dashboard surfaces the full list + mark-read controls.
 *
 * Why a global header bell rather than role-specific:
 *   * Every role gets server-driven alerts (booking confirmed for
 *     patients, new bookings for doctors, dispatch for ambulance, etc.)
 *   * Patients in particular shouldn't have to navigate home to find
 *     out their lab results landed — the count needs to be visible
 *     wherever they are in the SPA.
 *
 * Server-side notification policy lives in
 * `backend/notifications/signals.py`. Adding a new event = wire a new
 * `notify(...)` call there; the bell picks it up automatically.
 */
function NotificationBell({ role }) {
  // Map each role to the page where its Notifications card lives.
  // Fallback to /settings (visible to every authenticated user) when
  // we don't have a dedicated home for the role yet.
  const homeByRole = {
    doctor: '/doctor',
    specialist: '/doctor',
    patient: '/patient',
    pharmacy_staff: '/pharmacy',
    pharmacy: '/pharmacy',
    laboratory: '/lab',
    lab_staff: '/lab',
    radiology: '/radiology',
    radiology_staff: '/radiology',
    ambulance_staff: '/ambulance',
    org_admin: '/org/dashboard',
    admin: '/superuser/org-dashboard',
  }
  const target = homeByRole[role] || '/settings'

  const q = useQuery({
    queryKey: ['layout-notif-count'],
    queryFn: () => listNotifications({ unread: true }),
    // 10s polling so the bell feels close to real-time. Notifications
    // here include LAB_RESULT.READY / IMAGING.REPORT_READY / Rx-ready
    // events that need to surface promptly in a clinical context.
    // Real push (SSE/WebSocket) is the right fix at scale; this is the
    // cheap interim.
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
    staleTime: 5_000,
  })
  const unread = (q.data || []).length

  return (
    <Link
      to={target}
      title={unread === 0 ? 'No new notifications' : `${unread} unread notification${unread === 1 ? '' : 's'}`}
      aria-label={`Notifications (${unread} unread)`}
      className="relative inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/25 border border-white/15 transition-colors"
    >
      <BellIcon />
      {unread > 0 && (
        <span
          className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-coral-500 text-white text-[10px] font-bold leading-none border border-wantok-gradient"
          style={{ boxShadow: '0 0 0 1.5px rgba(15, 118, 110, 1)' }}
        >
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </Link>
  )
}

function BellIcon() {
  return (
    <svg viewBox="0 0 20 20" width="15" height="15" className="text-white/95">
      <path
        d="M10 3a4.5 4.5 0 0 0-4.5 4.5v2.4l-1.4 2.4a.7.7 0 0 0 .6 1H15.3a.7.7 0 0 0 .6-1l-1.4-2.4V7.5A4.5 4.5 0 0 0 10 3Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M8.3 14.5a1.7 1.7 0 0 0 3.4 0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

function SettingsIcon() {
  // Simple gear — stroked to match the SignOutIcon's visual weight.
  return (
    <svg viewBox="0 0 20 20" width="14" height="14" className="text-white/90">
      <path
        d="M10 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path
        d="M16.5 10c0-.4 0-.7-.1-1l1.4-1.1-1.5-2.6-1.7.7c-.5-.4-1-.7-1.6-.9L12.7 3h-3l-.3 1.8c-.6.2-1.1.5-1.6.9l-1.7-.7L4.6 7.6 6 8.7c-.1.3-.1.6-.1 1s0 .7.1 1L4.6 11.8l1.5 2.6 1.7-.7c.5.4 1 .7 1.6.9l.3 1.8h3l.3-1.8c.6-.2 1.1-.5 1.6-.9l1.7.7 1.5-2.6L16.4 11c.1-.3.1-.6.1-1Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  )
}
